/**
 * https://caniuse.com/?search=datetimeformat
 * Format a date according to the locale
 * @example
 * formatDateTime(new Date('2021-01-01T00:00:00.000Z'), 'pt-BR', { withTime: true })
 * returns '01/01/2021 | 00:00:00'
 * @param date be a Date object
 * @param locale be a string with the locale to be used
 * @returns formatted date according to the locale
 */

export type AllowedLocales = 'pt-BR' | 'en-US' | 'de-DE' | 'es-419'

export type AllowedFormats = 'date-time' | 'date' | 'time'
type DateType = Pick<
  Intl.DateTimeFormatPartTypesRegistry,
  'year' | 'month' | 'day'
>

type TimeType = Pick<
  Intl.DateTimeFormatPartTypesRegistry,
  'hour' | 'minute' | 'second'
>

type LocalePatternsType = {
  [key: string]: string
}

export const localePatterns: LocalePatternsType = {
  'pt-BR': '00/00/0000 | 00:00:00',
  'en-US': '00/00/0000 | 00:00:00 00',
  'de-DE': '00.00.0000 | 00:00:00',
  'es-419': '00/00/0000 | 00:00:00 00'
}

const formatDateTime = (
  date: Date,
  locale: AllowedLocales = 'pt-BR',
  format: AllowedFormats = 'date'
) => {
  const timeOptions: TimeType =
    format === 'date-time' || format === 'time'
      ? { hour: 'numeric', minute: 'numeric', second: 'numeric' }
      : ({} as Intl.DateTimeFormatPartTypesRegistry)

  const dateOptions: DateType =
    format === 'time'
      ? ({} as Intl.DateTimeFormatPartTypesRegistry)
      : { year: 'numeric', month: '2-digit', day: '2-digit' }

  const options: DateType & TimeType = {
    ...dateOptions,
    ...timeOptions
  }
  const intlDate = new Intl.DateTimeFormat(locale, options).format(date)

  return intlDate.replace(/^(.*\d)(\s|,\s)(\d.*)$/gm, '$1 | $3')
}

export default formatDateTime
